import type {
  CalendarCatalogResponse,
  CalendarReleaseSummary,
  CalendarUpgradeDiffResponse,
  ProjectDocument,
} from '@bigint-project/cloud-contracts';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import type { CloudApi, Etagged } from '../api/client';
import { errorMessage } from '../api/client';
import { EmptyMessage, ErrorMessage, Loading } from './Status';

interface CalendarPanelProps {
  readonly api: CloudApi;
  readonly project: ProjectDocument;
  readonly etag: string;
  readonly mutationBlocked: boolean;
  readonly onApplied: (result: Etagged<ProjectDocument>) => void;
}

function releaseLabel(release: CalendarReleaseSummary): string {
  return `${release.year} · ${release.revisionLabel} · ${release.sourceKey}`;
}

export function CalendarPanel({ api, project, etag, mutationBlocked, onApplied }: CalendarPanelProps): ReactElement {
  const [catalog, setCatalog] = useState<CalendarCatalogResponse | null>(null);
  const [calendarId, setCalendarId] = useState(project.project.defaultCalendarId as string);
  const [releaseId, setReleaseId] = useState('');
  const [diff, setDiff] = useState<CalendarUpgradeDiffResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canEdit = project.role !== 'viewer';
  const hasMissingYears = (diff?.missingYears.length ?? 0) > 0;

  useEffect(() => {
    let cancelled = false;
    void api.getCalendarCatalog().then(
      (result) => {
        if (!cancelled) {
          setCatalog(result);
          setReleaseId(result.releases[0]?.id ?? '');
        }
      },
      (reason: unknown) => {
        if (!cancelled) setError(errorMessage(reason));
      },
    ).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const selectedRelease = useMemo(
    () => catalog?.releases.find((release) => release.id === releaseId) ?? null,
    [catalog, releaseId],
  );
  const selectedSource = selectedRelease === null
    ? null
    : catalog?.sources.find((source) => source.key === selectedRelease.sourceKey) ?? null;

  const preview = async (): Promise<void> => {
    if (!canEdit || mutationBlocked || calendarId.length === 0 || releaseId.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      setDiff(await api.diffCalendar(project.id, {
        projectCalendarId: calendarId,
        calendarReleaseId: releaseId,
      }));
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  const apply = async (): Promise<void> => {
    if (!canEdit || mutationBlocked || diff === null) return;
    setBusy(true);
    setError(null);
    try {
      const result = diff.fromReleaseId === null
        ? await api.applyCalendar(project.id, {
          projectCalendarId: calendarId,
          calendarReleaseId: diff.toReleaseId,
        }, etag)
        : await api.upgradeCalendar(project.id, {
          projectCalendarId: calendarId,
          fromReleaseId: diff.fromReleaseId,
          toReleaseId: diff.toReleaseId,
        }, etag);
      onApplied(result);
      setDiff(null);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" aria-labelledby="calendar-catalog-heading">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Immutable calendar release</p>
          <h2 id="calendar-catalog-heading">政府行政機關辦公日曆</h2>
        </div>
        {!canEdit && <span className="muted">Viewer 可檢視，但不能套用</span>}
        {canEdit && mutationBlocked && <span className="muted">請先完成目前的雲端儲存</span>}
      </div>
      <p>
        適用政府行政機關；民間企業及特殊機關仍應依自身規則確認。套用後會固定該年度版本，
        新修正版不會自動改變既有排程。
      </p>
      {error !== null && <ErrorMessage message={error} />}
      {loading && <Loading />}
      {!loading && catalog?.releases.length === 0 && <EmptyMessage>目前沒有已發布的行事曆版本。</EmptyMessage>}

      {catalog !== null && catalog.releases.length > 0 && (
        <div className="calendar-controls">
          <label htmlFor="target-calendar">專案 Calendar</label>
          <select
            id="target-calendar"
            value={calendarId}
            disabled={!canEdit || mutationBlocked || busy}
            onChange={(event) => {
              setCalendarId(event.target.value);
              setDiff(null);
            }}
          >
            {project.project.calendars.map((calendar) => (
              <option key={calendar.id as string} value={calendar.id as string}>{calendar.name}</option>
            ))}
          </select>

          <label htmlFor="calendar-release">發布版本</label>
          <select
            id="calendar-release"
            value={releaseId}
            disabled={!canEdit || mutationBlocked || busy}
            onChange={(event) => {
              setReleaseId(event.target.value);
              setDiff(null);
            }}
          >
            {catalog.releases.map((release) => <option key={release.id} value={release.id}>{releaseLabel(release)}</option>)}
          </select>
          <button type="button" disabled={!canEdit || mutationBlocked || busy || releaseId.length === 0} onClick={() => void preview()}>
            比較差異
          </button>
        </div>
      )}

      {selectedSource !== null && selectedRelease !== null && (
        <aside className="source-card">
          <strong>{selectedSource.name}</strong>
          <span>發布：{new Date(selectedRelease.publishedAt).toLocaleString('zh-TW')}</span>
          <span>授權：{selectedSource.licenseName}</span>
          <a href={selectedSource.sourceUrl} target="_blank" rel="noreferrer">查看官方來源</a>
        </aside>
      )}

      {diff !== null && (
        <section className="calendar-diff" aria-label="行事曆差異">
          <h3>{diff.fromReleaseId === null ? '首次套用預覽' : '版本升級預覽'}</h3>
          <p className="muted">排程涵蓋年度：{diff.requiredYears.join('、') || '無'}</p>
          {hasMissingYears && (
            <div className="error-message" role="alert">
              <strong>缺少行事曆年度：{diff.missingYears.join('、')}</strong>
              <p>請先選擇並套用缺少年度的官方版本；系統不會用其他年度資料推算。</p>
            </div>
          )}
          <dl className="metrics">
            <div><dt>完成日（套用前）</dt><dd>{diff.projectFinishBefore}</dd></div>
            <div><dt>完成日（套用後）</dt><dd>{diff.projectFinishAfter}</dd></div>
            <div><dt>受影響任務</dt><dd>{diff.changedTaskCount}</dd></div>
          </dl>
          <ul className="change-list">
            {diff.changes.map((change) => (
              <li key={`${change.date}-${change.name}`}>
                <strong>{change.date}</strong> {change.name}：
                {change.beforeWorking ? '工作' : '休假'} → {change.afterWorking ? '工作' : '休假'}
                {change.maskedByManual && <span className="muted">（由手動例外優先）</span>}
              </li>
            ))}
          </ul>
          <button type="button" disabled={!canEdit || mutationBlocked || busy || hasMissingYears} onClick={() => void apply()}>
            {hasMissingYears ? '缺少年度，無法套用' : busy ? '套用中…' : '確認套用並重排程'}
          </button>
        </section>
      )}
    </section>
  );
}
